import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	HostListener,
	inject,
	OnInit,
	QueryList,
	Signal,
	signal,
	viewChild,
	ViewChildren,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSliderModule } from '@angular/material/slider';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SHARED_MODULES } from '../../../../shared.';

// Models
import { IConfirmModalData } from '../../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../../modals/confirm-modal/models/confirmModalAnswer';

// Services
import { Utils } from '../../../services/utils.service';
import { ProductsService } from '../../../services/products.service';

// Components
import { PhButtonComponent } from '../../ph-button/ph-button.component';
import { PhIconComponent } from '../../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../../ph-loader/ph-loader.component';
import { PhotoEditorComponent } from '../photo-editor/photo-editor.component';
import { ConfirmModalComponent } from '../../../modals/confirm-modal/confirm-modal.component';

// Pipes
import { SanitizerPipe } from '../../../pipes/sanitizer.pipe';
import { MeasurementUnitTranslatePipe } from '../../../pipes/measurement-unit-translate.pipe';
import { FileSizePipe } from '../../../pipes/file-size.pipe';

// Utils
import {
	deriveImageFileNameFromSrc,
	formatProductBarcodeList,
	productSizeAspectRatio,
	resolveImageByteSize,
	revokeBlobObjectUrl
} from '../product-photo.utils';

export interface ProductPhotoSelectPayload {
	id: number;
	name: string;
	barcode: string;
	article: string;
	isMasterPosition?: boolean;
	width: number;
	height: number;
}

export interface ProductPhotoPickModalData {
	product: ProductPhotoSelectPayload;
	initialImageSrc?: string;
}

type ImageSourceType = 'google' | 'file' | undefined;

@Component({
	selector: 'app-product-photo-pick-modal',
	templateUrl: './product-photo-pick-modal.component.html',
	styleUrls: ['./product-photo-pick-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		SHARED_MODULES,
		MatStepperModule,
		MatSliderModule,
		NgxFileDropModule,
		SanitizerPipe,
		FaIconComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhotoEditorComponent,
		MeasurementUnitTranslatePipe,
		FileSizePipe
	]
})
export class ProductPhotoPickModalComponent implements OnInit, AfterViewInit {
	private readonly matData: ProductPhotoPickModalData = inject<ProductPhotoPickModalData>(MAT_DIALOG_DATA);
	private readonly dialogRef: MatDialogRef<ProductPhotoPickModalComponent, string | false> = inject(MatDialogRef<ProductPhotoPickModalComponent, string | false>);
	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);
	private readonly products: ProductsService = inject(ProductsService);

	private googleLoaded: boolean = false;
	private closeConfirmOpened: boolean = false;

	readonly cropEditor: Signal<PhotoEditorComponent | undefined> = viewChild<PhotoEditorComponent>('photoEditorNode');

	@ViewChildren('thumbBtn', { read: ElementRef }) thumbBtnRefs?: QueryList<ElementRef<HTMLElement>>;

	readonly product: WritableSignal<ProductPhotoSelectPayload> = signal(
		this.matData.product ?? { id: 0, name: '', barcode: '', article: '', width: 0, height: 0 }
	);
	readonly googleImages: WritableSignal<string[]> = signal([]);
	readonly loader: WritableSignal<boolean> = signal(true);

	readonly googleIndex: WritableSignal<number> = signal(0);
	readonly imageType: WritableSignal<ImageSourceType> = signal(undefined);

	readonly uploadedImage: WritableSignal<File | undefined> = signal(undefined);
	readonly uploadedImageUrl: WritableSignal<string> = signal('');
	readonly existingImageSizeBytes: WritableSignal<number | null> = signal(null);

	readonly mainPanelDragDepth: WritableSignal<number> = signal(0);
	readonly mainDropWarning: WritableSignal<boolean> = signal(false);
	readonly mainImageError: WritableSignal<boolean> = signal(false);

	readonly cropStepActive: WritableSignal<boolean> = signal(false);
	readonly cropEditorImageSrc: WritableSignal<string> = signal('');

	readonly tileIconPlaceholderColor = '#FF5A00';

	readonly productBarcodeText: Signal<string> = computed((): string => formatProductBarcodeList(this.product()?.barcode));
	readonly showProductBarcodes: Signal<boolean> = computed((): boolean => Boolean(this.productBarcodeText()) && !this.product()?.isMasterPosition);

	readonly productArticleText: Signal<string> = computed((): string => this.product()?.article);
	readonly showProductArticle: Signal<boolean> = computed((): boolean => Boolean(this.productArticleText()) && !this.product()?.isMasterPosition);

	readonly mainDropActive: Signal<boolean> = computed((): boolean => this.mainPanelDragDepth() > 0);
	readonly pickerStepIndex: Signal<0 | 1> = computed((): 0 | 1 => (this.cropStepActive() ? 1 : 0));

	readonly isTileActive: Signal<boolean> = computed((): boolean =>
		this.imageType() === 'file' || (this.imageType() === 'google' && this.googleImages().length > 0)
	);
	readonly showMainPreview: Signal<boolean> = computed((): boolean => !!this.mainPreviewSrc() && !this.mainImageError());

	readonly hasImageForCrop: Signal<boolean> = computed((): boolean => {
		const type: ImageSourceType = this.imageType();
		if (type === 'file') return Boolean(this.uploadedImageUrl());
		if (type === 'google' && this.googleImages().length) return Boolean(this.googleImages()[this.googleIndex()]);
		return false;
	});

	readonly mainPreviewSrc: Signal<string> = computed((): string => {
		const type: ImageSourceType = this.imageType();
		if (type === 'file') return this.uploadedImageUrl();
		if (type === 'google' && this.googleImages().length) return this.googleImages()[this.googleIndex()] ?? '';
		return '';
	});

	readonly displayedFileName: Signal<string> = computed((): string => {
		const uploaded: File = this.uploadedImage();
		return uploaded?.name ? uploaded.name : deriveImageFileNameFromSrc(this.mainPreviewSrc());
	});

	readonly displayedFileSizeBytes: Signal<number> = computed((): number => this.uploadedImage()?.size ?? this.existingImageSizeBytes() ?? 0);

	readonly cropAspectRatio: Signal<number> = computed((): number => productSizeAspectRatio(this.product()?.width, this.product()?.height));

	readonly resetAllDisabled: Signal<boolean> = computed((): boolean => {
		const editor: PhotoEditorComponent = this.cropEditor();
		return editor ? !editor.hasUnsavedChanges() : true;
	});

	constructor() {
		this.destroyRef.onDestroy((): void => revokeBlobObjectUrl(this.uploadedImageUrl()));
	}

	@HostListener('window:keydown', ['$event'])
	onWindowKeydown(event: KeyboardEvent): void {
		if (this.cropStepActive()) return;
		if (this.imageType() !== 'google' || this.googleImages().length === 0) return;
		if (event.code !== 'ArrowLeft' && event.code !== 'ArrowRight') return;
		if (ProductPhotoPickModalComponent.isEditableKeydownTarget(event.target)) return;
		if (event.ctrlKey || event.metaKey || event.altKey) return;

		event.preventDefault();
		this.rotateGoogleIndex(event.code === 'ArrowLeft' ? -1 : 1);
	}

	private static isEditableKeydownTarget(target: EventTarget | null): boolean {
		if (!(target instanceof Element)) return false;
		return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
	}

	ngOnInit(): void {
		if (this.matData.initialImageSrc) {
			this.imageType.set('file');
			this.uploadedImageUrl.set(this.matData.initialImageSrc);
			this.cropEditorImageSrc.set(this.matData.initialImageSrc);
			this.cropStepActive.set(true);

			void this.resolveExistingImageSize(this.matData.initialImageSrc);
		}

		void this.loadGoogleImages();
	}

	ngAfterViewInit(): void {
		this.dialogRef.disableClose = true;

		this.dialogRef.backdropClick()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((): void => this.onCloseModal());

		this.dialogRef.keydownEvents()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((event: KeyboardEvent): void => {
				if (event.key === 'Escape') {
					event.preventDefault();
					event.stopPropagation();
					this.onCloseModal();
				}
			});

		this.thumbBtnRefs?.changes
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((): void => this.queueScrollActiveThumb());

		this.queueScrollActiveThumb();
	}

	private async loadGoogleImages(): Promise<void> {
		if (this.googleLoaded) return;
		this.googleLoaded = true;
		this.loader.set(true);

		try {
			const p: ProductPhotoSelectPayload = this.product();
			const raw = await this.products.googleImages(p.name, p.barcode);
			const urls: Array<string> = this.products.normalizeGoogleImagesResponse(raw);

			this.googleImages.set(urls);
			if (urls.length && !this.imageType()) {
				this.imageType.set('google');
			}
		}
		finally {
			this.loader.set(false);
		}
	}

	onCancel(): void {
		if (this.cropStepActive()) {
			this.leaveCropStep();
			return;
		}
		this.dialogRef.close(false);
	}

	onCloseModal(): void {
		if (!this.hasUnsavedCropChanges()) {
			this.dialogRef.close(false);
			return;
		}

		if (this.closeConfirmOpened) return;
		this.closeConfirmOpened = true;

		const confirmData: IConfirmModalData = {
			label: 'MUST_SAVE_CHANGES',
			confirmLabel: 'YES',
			cancelLabel: 'NO',
			twoAnswers: true
		};

		this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '420px',
			maxWidth: '92vw',
			data: confirmData,
			autoFocus: false,
			backdropClass: 'back-drop'
		}).afterClosed().subscribe((result: IConfirmModalAnswer): void => {
			this.closeConfirmOpened = false;
			if (result?.answer === true) {
				this.dialogRef.close(false);
			}
		});
	}

	selectGoogle(index: number): void {
		if (index < 0 || index >= this.googleImages().length) return;

		this.googleIndex.set(index);
		this.imageType.set('google');
		this.queueScrollActiveThumb();
	}

	googlePrev(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.rotateGoogleIndex(-1);
	}

	googleNext(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.rotateGoogleIndex(1);
	}

	private rotateGoogleIndex(delta: number): void {
		const n: number = this.googleImages().length;
		if (!n) return;

		this.googleIndex.update((i: number): number => (i + delta + n) % n);
		this.queueScrollActiveThumb();
	}

	private queueScrollActiveThumb(): void {
		queueMicrotask((): void => this.scrollActiveThumbIntoView());
	}

	private scrollActiveThumbIntoView(): void {
		if (this.imageType() !== 'google' || !this.googleImages().length) return;

		const list: QueryList<ElementRef<HTMLElement>> = this.thumbBtnRefs;
		const idx: number = this.googleIndex();

		if (!list?.length || idx < 0 || idx >= list.length) return;

		const el: HTMLElement = list.get(idx)?.nativeElement;
		el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
	}

	onMainDragEnter(event: DragEvent): void {
		event.preventDefault();
		this.mainPanelDragDepth.update((d: number): number => d + 1);
	}

	onMainDragOver(event: DragEvent): void {
		event.preventDefault();
	}

	onMainDragLeave(): void {
		this.mainPanelDragDepth.update((d: number): number => Math.max(0, d - 1));
	}

	chooseFile(files: NgxFileDropEntry[]): void {
		this.mainPanelDragDepth.set(0);
		this.mainImageError.set(false);
		this.mainDropWarning.set(false);

		if (!files.length || !files[0].fileEntry.name || !Utils.bruteCheckFileExtension(files[0].fileEntry.name)) {
			this.mainDropWarning.set(true);
			return;
		}

		(files[0].fileEntry as FileSystemFileEntry).file((file: File): void => {
			revokeBlobObjectUrl(this.uploadedImageUrl());

			this.imageType.set('file');
			this.uploadedImage.set(file);
			this.existingImageSizeBytes.set(null);
			this.uploadedImageUrl.set(URL.createObjectURL(file));
			this.mainImageError.set(false);
		});
	}

	onMainPanelClick(openFileSelector: () => void): void {
		this.mainDropWarning.set(false);

		if (this.mainImageError() ||
			(this.imageType() === 'file' && !this.uploadedImageUrl()) ||
			(this.imageType() === 'google' && !this.googleImages().length) ||
			!this.imageType()) {
			openFileSelector();
		}
	}

	onChangeFileClick(event: Event, openFileSelector: () => void): void {
		event.stopPropagation();
		this.mainDropWarning.set(false);
		this.mainImageError.set(false);
		openFileSelector();
	}

	onMainImageError(): void {
		this.mainImageError.set(true);
		revokeBlobObjectUrl(this.uploadedImageUrl());
		this.uploadedImageUrl.set('');
	}

	removeFilePhoto(event: MouseEvent): void {
		event.stopPropagation();
		revokeBlobObjectUrl(this.uploadedImageUrl());

		this.uploadedImage.set(undefined);
		this.uploadedImageUrl.set('');
		this.existingImageSizeBytes.set(null);
		this.mainDropWarning.set(false);
		this.cropStepActive.set(false);
		this.imageType.set(undefined);
		this.mainImageError.set(false);
	}

	enterCropStep(): void {
		const src: string = this.mainPreviewSrc();
		if (!src) return;
		this.cropEditorImageSrc.set(src);
		this.cropStepActive.set(true);
	}

	leaveCropStep(): void {
		this.cropStepActive.set(false);
		this.cropEditorImageSrc.set('');
	}

	onMaskMode(mode: 'product' | 'image' | 'free'): void {
		this.cropEditor()?.setMaskMode(mode);
	}

	onZoomIn(): void {
		this.cropEditor()?.zoomIn();
	}

	onZoomOut(): void {
		this.cropEditor()?.zoomOut();
	}

	onRotate90(dir: -1 | 1): void {
		this.cropEditor()?.rotateBy90(dir);
	}

	onRotationInput(value: number | string): void {
		const deg: number = Number(value);
		if (Number.isFinite(deg)) {
			this.cropEditor()?.setRotationDeg(deg);
		}
	}

	onResetAll(): void {
		if (this.resetAllDisabled()) return;
		this.cropEditor()?.resetAll();
	}

	onCropClick(): void {
		this.enterCropStep();
	}

	onPickerStepChange(event: StepperSelectionEvent): void {
		this.onMaskMode('product');

		if (event.selectedIndex === 0) {
			this.leaveCropStep();
		} else if (event.selectedIndex === 1) {
			this.enterCropStep();
			setTimeout((): void => {
				this.cropEditor()?.resetAll();
			}, 0);
		}
	}

	async onCropApply(): Promise<void> {
		const editor: PhotoEditorComponent = this.cropEditor();
		if (!editor) return;

		const dataUrl: string = await editor.getEditedImageDataUrl();
		if (dataUrl) {
			this.dialogRef.close(dataUrl);
		}
	}

	private async resolveExistingImageSize(src: string): Promise<void> {
		if (!src || this.uploadedImage()) {
			this.existingImageSizeBytes.set(null);
			return;
		}

		try {
			this.existingImageSizeBytes.set(await resolveImageByteSize(src));
		}
		catch {
			this.existingImageSizeBytes.set(null);
			this.mainImageError.set(true);
		}
	}

	private hasUnsavedCropChanges(): boolean {
		return this.cropStepActive() && !this.resetAllDisabled();
	}
}